import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import { app } from '@df/core/app';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { ColorOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ReadMoreComponent } from '../../../ui/common/read-more.component';
import { Product } from '../product.decorator';
import { IProductMediaItem } from '../view-media/product-view-media.component';
export interface IProductRangeBanner extends NICmsContentComponent.IDataWithStyles {
    items: IProductRangeBannerItem[];
    titleColor?: string;
    descColor?: string;
}
export interface IProductRangeBannerItem {
    hidden?: boolean;
    mediaType?: 'image' | 'video';
    image?: string;
    video?: string;
    title?: string;
    desc?: string;
    showVideoControlPanel?: boolean;
}
@Component({
    selector: 'product-range-banner',
    templateUrl: './product-range-banner.html',
    styleUrl: './product-range-banner.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [IfSizeDirective, CommonModule, ReadMoreComponent, ImageModule, CarouselModule, GridModule, VimeoPlayerComponent]
})
@ICmsComponent<ProductRangeBannerComponent>({
    id: 'product-range-banner',
    block: {
        url: 'product-range-banner'
    },
    name: 'Product Range Banner',
    description: 'Product Range Banner Block',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-specification',
            title: 'Range Banner'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'items',
            title: 'Items',
            description: 'Items are overwritten with product data if configured. Save changes to show it outside the editor.',
            itemName: 'Item',
            itemNameField: 'title',
            maxSize: 4,
            fields: [
                {
                    type: NICmsContentField.EFieldType.choice,
                    key: 'mediaType',
                    title: 'Media Type',
                    options: [
                        { key: 'image', title: 'Image' },
                        { key: 'video', title: 'Video' }
                    ],
                    default: 'image'
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image',
                    description: 'Used as main image or video placeholder',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'video',
                    title: 'Vimeo Video URL',
                    description: 'Full Vimeo video URL (e.g., https://vimeo.com/123456789)',
                    canShow: data => data?.mediaType === 'video'
                },
                {
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'showVideoControlPanel',
                    title: 'Show Video Controls',
                    description: 'Display video control panel (play/pause buttons)',
                    labels: { true: 'Show Controls', false: 'Hide Controls' },
                    canShow: data => data?.mediaType === 'video',
                    default: false
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'desc',
                    title: 'Description'
                },
                {
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'hidden',
                    title: 'Hidden',
                    labels: { true: 'Hidden', false: 'Visible' },
                    default: false
                }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'titleColor',
            title: 'Title Color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            options: ColorOptions
        },
        <NICmsContentField.IChoiceField>{
            key: 'descColor',
            title: 'Description Color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            options: ColorOptions
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.bg',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background Color',
            options: () => [...UndefinedOption, ...app.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).colors],
            colorPreview: true
        }
    ]
})
export class ProductRangeBannerComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: IProductRangeBanner;

    @Input()
    product?: Product<IProductDataV3>;

    @Input()
    variant?: string;

    get hasProductRangeImages(): boolean {
        return !!(
            this.firstProductRangeImage ||
            this.secondProductRangeImage ||
            this.thirdProductRangeImage ||
            this.fourthProductRangeImage
        );
    }

    get hasAnyItemWithImage(): boolean {
        return this.items.some(item => !!item.image);
    }

    get firstProductRangeImage(): IProductMediaItem | undefined {
        return this.findMediaByLabel('image_for_product_range_block_');
    }

    get secondProductRangeImage(): IProductMediaItem | undefined {
        return this.findMediaByLabel('image_for_product_range_block_2');
    }

    get thirdProductRangeImage(): IProductMediaItem | undefined {
        return this.findMediaByLabel('image_for_product_range_block_3');
    }

    get fourthProductRangeImage(): IProductMediaItem | undefined {
        return this.findMediaByLabel('image_for_product_range_block_4');
    }

    private findMediaByLabel(label: string): IProductMediaItem | undefined {
        if (!this.product?.familyMedia) return undefined;

        return this.product?.familyMedia && this.product.familyMedia.find(media => media.labels?.find(l => l === label));
    }

    get items(): IProductRangeBannerItem[] {
        if (this.hasProductRangeImages) {
            const productRangeItems: IProductRangeBannerItem[] = [];

            [this.firstProductRangeImage, this.secondProductRangeImage, this.thirdProductRangeImage, this.fourthProductRangeImage].forEach(
                (image, index) => {
                    if (image) {
                        const isVideo = image.video;
                        productRangeItems.push({
                            mediaType: isVideo ? 'video' : 'image',
                            ...(isVideo
                                ? { video: image.video, showVideoControlPanel: this.showVideoControlPanel(image) }
                                : { image: image.image }),
                            title: this.product?.data?.[
                                `title_product_range_block${index ? '_' + (index + 1) : ''}` as keyof IProductDataV3
                            ] as string,
                            desc: this.product?.data?.[
                                `text_field_product_range_block${index ? '_' + (index + 1) : ''}` as keyof IProductDataV3
                            ] as string
                        });
                    } else {
                        productRangeItems.push({
                            hidden: true
                        });
                    }
                }
            );

            return productRangeItems;
        } else {
            return this.processIcmsItems(this.content?.items || []);
        }
    }

    get visibleItemsCount(): number {
        return this.items.filter(item => !item.hidden).length;
    }

    get visibleItemsInFirstThree(): number {
        return this.items.slice(0, 3).filter(item => !item.hidden).length;
    }

    private processIcmsItems(items: IProductRangeBannerItem[]): IProductRangeBannerItem[] {
        const normalizedItems: IProductRangeBannerItem[] = [];

        for (let i = 0; i < 4; i++) {
            const item = items[i];
            if (item) {
                normalizedItems.push({
                    ...item,
                    mediaType: item.mediaType || 'image',
                    hidden: item.hidden || (!item.image && !item.video),
                    ...(item.mediaType === 'video' && item.video
                        ? {
                              video: item.video,
                              showVideoControlPanel: item.showVideoControlPanel || false
                          }
                        : {})
                });
            } else {
                // Add hidden placeholder for missing items
                normalizedItems.push({ hidden: true });
            }
        }

        return normalizedItems;
    }

    showVideoControlPanel(item: IProductMediaItem): boolean {
        return !!item.labels?.includes('show_videocontrolpanel');
    }
}
